import { defineComponent as _defineComponent } from 'vue';
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref } from "vue";
const _hoisted_1 = { class: "upload-section" };
const _hoisted_2 = { class: "d-flex align-center ga-2" };
const _hoisted_3 = {
    key: 1,
    class: "text-caption text-medium-emphasis d-flex align-center text-no-wrap"
};
const _hoisted_4 = ["accept", "disabled"];
import { ref } from "vue";
import ChartPagePickerDialog from "@/components/ChartPagePickerDialog.vue";
import ChartPrepareStatus from "@/components/ChartPrepareStatus.vue";
import { canChoosePagesFrom, failureMessage, prepareChartFromKey, subsetChart, uploadChartFile, } from "@/composables/useChartUpload";
import { parseUploadError } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
/**
 * Uploader for a supporting-document slot whose file reaches CoverMyMeds: browser → S3, then the prepare
 * job against the budget left by the other documents on the form, with the page picker when even that
 * fails. Emits the downloadLink payload UploadFileComponent emits, plus what the job did to the file.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartUploadFileComponent',
    props: {
        storeVar: { default: "" },
        label: { default: "" },
        storeVarIndex: { default: null },
        otherLinks: { default: () => [] },
        disabled: { type: Boolean, default: false },
        accept: { default: ".tif, .tiff, .pdf, .png, .jpg, .jpeg" }
    },
    emits: ["downloadLink", "uploadError"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const fileInput = ref(null);
        const uploading = ref(false);
        const preparing = ref(false);
        const error = ref(null);
        const errorDetails = ref(null);
        const originBytes = ref(null);
        const sizeBytes = ref(null);
        const compressionLevel = ref(null);
        const pickerOpen = ref(false);
        let currentFile = null;
        const fail = (message) => {
            error.value = message;
            emit("uploadError", message);
        };
        const settle = (outcome, file) => {
            if (outcome.status === "error") {
                errorDetails.value = outcome.errorDetails;
                fail(failureMessage(outcome, props.otherLinks.length > 0));
                return;
            }
            originBytes.value = file.size;
            sizeBytes.value = outcome.sizeBytes;
            compressionLevel.value = outcome.level ?? null;
            emit("downloadLink", {
                link: outcome.downloadUrl,
                storeVar: props.storeVar,
                label: props.label,
                storeVarIndex: props.storeVarIndex,
                fileName: file.name,
                uploadKey: outcome.uploadKey,
                sizeBytes: outcome.sizeBytes,
                pageCount: outcome.pageCount,
                pageRanges: outcome.pageRanges,
                droppedPages: outcome.droppedPages,
            });
        };
        const onChange = async (event) => {
            const input = event.target;
            const file = input.files?.[0];
            if (!file)
                return;
            error.value = null;
            errorDetails.value = null;
            originBytes.value = null;
            sizeBytes.value = null;
            compressionLevel.value = null;
            uploading.value = true;
            let uploaded;
            try {
                uploaded = await uploadChartFile(file);
            }
            catch (uploadFailure) {
                fail(parseUploadError(uploadFailure));
                input.value = "";
                return;
            }
            finally {
                uploading.value = false;
            }
            currentFile = file;
            preparing.value = true;
            try {
                settle(await prepareChartFromKey(uploaded.uploadKey, { otherLinks: props.otherLinks }), file);
            }
            catch (requestFailure) {
                fail(tryParseApiErrors(requestFailure)[0]);
            }
            finally {
                preparing.value = false;
                if (error.value)
                    input.value = "";
            }
        };
        const onConfirmPages = async (pages) => {
            pickerOpen.value = false;
            const bestUploadKey = errorDetails.value?.bestUploadKey;
            const file = currentFile;
            if (!bestUploadKey || !file)
                return;
            error.value = null;
            preparing.value = true;
            try {
                settle(await subsetChart(bestUploadKey, pages, { otherLinks: props.otherLinks }), file);
            }
            catch (requestFailure) {
                fail(tryParseApiErrors(requestFailure)[0]);
            }
            finally {
                preparing.value = false;
            }
        };
        return (_ctx, _cache) => {
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    (uploading.value)
                        ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                            key: 0,
                            indeterminate: "",
                            color: "primary",
                            size: "20",
                            width: "2"
                        }))
                        : (preparing.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                                _createVNode(_component_v_progress_circular, {
                                    indeterminate: "",
                                    size: "12",
                                    width: "2",
                                    class: "mr-2"
                                }),
                                _cache[2] || (_cache[2] = _createTextVNode(" Preparing document… ", -1))
                            ]))
                            : _createCommentVNode("", true),
                    _createElementVNode("input", {
                        ref_key: "fileInput",
                        ref: fileInput,
                        type: "file",
                        accept: __props.accept,
                        disabled: __props.disabled || uploading.value || preparing.value,
                        onChange: onChange
                    }, null, 40, _hoisted_4)
                ]),
                _createVNode(ChartPrepareStatus, {
                    error: error.value,
                    "origin-bytes": originBytes.value,
                    "size-bytes": sizeBytes.value,
                    "compression-level": compressionLevel.value,
                    "can-choose-pages": _unref(canChoosePagesFrom)(errorDetails.value) && !preparing.value,
                    onChoosePages: _cache[0] || (_cache[0] = ($event) => (pickerOpen.value = true))
                }, null, 8, ["error", "origin-bytes", "size-bytes", "compression-level", "can-choose-pages"]),
                (errorDetails.value?.bestDownloadUrl && errorDetails.value?.perPageBytes)
                    ? (_openBlock(), _createBlock(ChartPagePickerDialog, {
                        key: 0,
                        modelValue: pickerOpen.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((pickerOpen).value = $event)),
                        "download-url": errorDetails.value.bestDownloadUrl,
                        "per-page-bytes": errorDetails.value.perPageBytes,
                        "budget-bytes": errorDetails.value.budgetBytes,
                        onConfirm: onConfirmPages
                    }, null, 8, ["modelValue", "download-url", "per-page-bytes", "budget-bytes"]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
